import { NotImplemented } from '../../errors'
import { createEventBus, EventsTrait } from '../../events/bus'
// import { SDK_CONCURRENT_PLAYBACK_STOP } from '../../events/types/sdk.events'
import { VIDEO_PLAYBACK_INITIATED, VIDEO_PLAYER_MOUNTED, VIDEO_PLAYER_UNMOUNTED } from '../../events/types/video.events'

import logger, { Logger } from '../../utils/logger'

import { Chapters } from './chapters'

import type { Chapter } from './chapters'

export interface Config {
  preview?: string
  autoplay: boolean
  width?: string | number,
  height?: string | number,
  sdk: {
    applicationId: string
    version?: string
  },
  chromecast?: {
    receiverId: string,
  },
  drm?: {
    sessionId?: string
  },
  mode?: 'stash' | 'release' | 'mixed',
  meta?: {
    title: string
    artist: string,
    date: string,
  }
}

export type Location = { page: string, section: string }

export interface NugsVendorVideoPlayer<T> {
  src: T

  readonly video?: HTMLVideoElement
  readonly logger: Logger
  readonly config: Config

  mount<T>(_el?: HTMLVideoElement): Promise<T | void | undefined>
  unmount<T>(): Promise<T | void | undefined>
  load<T>(): Promise<T | void | undefined>
  init<T>(): Promise<T | void | undefined>
  stop(): void
  pause(): void
  play(): void
  seek(time: number): void
}

export type PlayerSrc = { dash: string, hls: string } | string

export abstract class VendorVideoPlayer<T> extends EventsTrait implements NugsVendorVideoPlayer<T> {
  #events = createEventBus()

  video!: HTMLVideoElement

  #src!: T
  #chapters: Chapters
  #userId: string | number
  #config: Config
  #started: boolean

  get src() {
    return this.#src
  }

  set src(value: T) {
    this.#src = value
  }

  protected get userId() {
    return this.#userId
  }

  public get currentTime() {
    return this.video.currentTime
  }

  public set chapters(value: Chapter[]) {
    this.#chapters?.use(value)
  }

  public get chapters(): Chapters {
    return this.#chapters
  }

  public get config() {
    return this.#config
  }

  public get events() {
    return this.#events
  }

  public static get logger() {
    return logger
  }

  public get logger() {
    return logger
  }

  protected get element(): HTMLVideoElement {
    throw new NotImplemented
  }

  constructor(config: Config, userId: string | number, chapters: Chapter[] = [], duration = 0) {
    super()

    this.#started = false
    this.#config = config
    this.#userId = userId
    this.#chapters = new Chapters(chapters, this.events, duration)

    this.#handleEvents()
  }

  #handleEvents() {
    const onStarted = () => {
      if (!this.#started) {
        this.#chapters.use(this.chapters.list)
        this.events.dispatchEvent(VIDEO_PLAYBACK_INITIATED)
      }

      this.#started = true
    }

    const onDurationChange = () => {
      this.#chapters.duration = this.video.duration
    }

    const onEnded = () => this.#started = false

    const videoPlayerMounted = () => {
      this.#chapters.duration = this.video.duration
      this.#chapters.use(this.chapters.list)

      this.video.addEventListener('durationchange', onDurationChange)
      this.video.addEventListener('playing', onStarted)
      this.video.addEventListener('ended', onEnded)
    }

    const videoPlayerUnmounted = () => {
      if (!this.video) return
      this.video.removeEventListener('playing', onStarted)
      this.video.removeEventListener('ended', onEnded)
      this.video.removeEventListener('durationchange', onDurationChange)
    }

    this.on(VIDEO_PLAYER_MOUNTED, videoPlayerMounted)
    this.on(VIDEO_PLAYER_UNMOUNTED, videoPlayerUnmounted)
    // this.on(SDK_CONCURRENT_PLAYBACK_STOP, () => this.pause())
  }

  public abstract mount<T>(_el?: HTMLVideoElement | HTMLElement): Promise<T | void | undefined>
  public abstract unmount<T>(): Promise<T | void | undefined>
  public abstract load<T>(): Promise<T | void | undefined>
  public abstract init<T>(): Promise<T | void | undefined>

  public abstract stop(): void
  public abstract pause(): void
  public abstract play(): void
  public abstract seek(time: number): void
}
